import React, { useState, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import CloseIcon from "@mui/icons-material/Close";
import { Formik, Form } from "formik";
import ScannerInput from "../../../form/ScannerInput";
import SerialNumbersList from "./SerialNumbersList";

const ErrorModal = ({ open, onClose, message }) => {
  const styles = {
    dialogTitle: {
      position: "relative",
      textAlign: "center",
      pb: 1,
    },
    closeIcon: {
      position: "absolute",
      right: 8,
      top: 8,
    },
    errorTitle: {
      color: "error.main",
      fontSize: 24,
      fontWeight: 500,
    },
    dialogContent: {
      textAlign: "center",
      p: 4,
    },
    errorMessage: {
      fontSize: 18,
      lineHeight: 1.4,
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.errorTitle}>Error</Typography>
        <IconButton onClick={onClose} sx={styles.closeIcon}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Typography sx={styles.errorMessage}>{message}</Typography>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmCancelModal = ({ open, onClose, onConfirm }) => {
  const styles = {
    dialogTitle: {
      position: "relative",
      textAlign: "center",
      pb: 1,
    },
    closeIcon: {
      position: "absolute",
      right: 8,
      top: 8,
    },
    warningTitle: {
      color: "text.primary",
      fontSize: 22,
      fontWeight: 500,
    },
    dialogContent: {
      textAlign: "center",
      p: 4,
    },
    warningMessage: {
      fontSize: 18,
      lineHeight: 1.4,
    },
    dialogActions: {
      justifyContent: "center",
      gap: 2,
      px: 3,
      pb: 3,
    },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.warningTitle}>Confirm Cancel</Typography>
        <IconButton onClick={onClose} sx={styles.closeIcon}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        <Typography sx={styles.warningMessage}>
          You have scanned serial numbers that will be lost. Are you sure you want to cancel?
        </Typography>
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={onClose} variant="outlined">
          Go Back
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          Yes, Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const styles = {
  dialogTitle: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },

  dialogActions: {
    px: 3,
    pb: 2,
    pt: 2,
    justifyContent: "space-between",
  },

  linearProgress: {
    backgroundColor: theme => theme.palette.grey[200],
  },
};

export default function SerialNumbersModal({
  open,
  onClose,
  quantity,
  onSave,
  initialSerialNumbers = [],
  existingShipmentSerialNumbers = [],
}) {
  const [serials, setSerials] = useState(initialSerialNumbers);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const inputRef = useRef(null);
  const normalizedShipmentSerials = existingShipmentSerialNumbers
    .map(serial => (serial || "").trim())
    .filter(Boolean);

  const handleAddSerial = code => {
    const normalized = (code || "").trim();
    
    if (!normalized) return;
    
    if (serials.length >= quantity) return;
    
    // Check for duplicate
    if (serials.includes(normalized)) {
      setErrorMessage("This serial number has already been scanned.");
      return;
    }

    if (normalizedShipmentSerials.includes(normalized)) {
      setErrorMessage("This serial number already exists on another item in this shipment.");
      return;
    }
    
    setSerials(prev => [...prev, normalized]);
  };

  const handleRemoveSerial = idx => {
    setSerials(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    onSave(serials);
  };

  const handleCancelClick = () => {
    if (serials.length > 0) {
      setShowConfirmCancel(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setShowConfirmCancel(false);
    onClose();
  };

  const progress = quantity > 0 ? (serials.length / quantity) * 100 : 0;
  const isComplete = serials.length >= quantity && quantity > 0;

  return (
    <Dialog open={open} onClose={() => {}} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={styles.dialogTitle}>
        <Typography variant="h2" color="primary">
          {serials.length} / {quantity}
        </Typography>
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          scanned
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="center" pb={3}>
          <Box width="100%" mt={2}>
            <Formik
              initialValues={{ barcode: "" }}
              onSubmit={(values, { resetForm }) => {
                handleAddSerial(values.barcode);
                resetForm();
              }}
            >
              {({ setFieldValue, submitForm }) => (
                <Form>
                  <ScannerInput
                    name="barcode"
                    label={isComplete ? "Scanning Complete" : "Scan serial number"}
                    disabled={isComplete}
                    autoFocus
                    size="large"
                    inputRef={inputRef}
                    onDetected={code => {
                      handleAddSerial(code);
                      setFieldValue("barcode", "");
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => {
                              submitForm();
                              setTimeout(() => inputRef.current?.focus(), 0);
                            }}
                            disabled={isComplete}
                            edge="end"
                            size="small"
                            color="primary"
                          >
                            <KeyboardReturnIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Form>
              )}
            </Formik>
          </Box>

          {serials.length > 0 && (
            <Box mt={3} width="100%">
              <Box mt={3}>
                <SerialNumbersList serials={serials} onRemove={handleRemoveSerial} />
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <LinearProgress
        variant="determinate"
        value={progress}
        color="primary"
        sx={styles.linearProgress}
      />
      <DialogActions sx={styles.dialogActions}>
        <Button onClick={handleCancelClick} variant="outlined">
          Cancel
        </Button>
        <Button variant="contained" disabled={serials.length === 0} onClick={handleSave} color="primary">
          Save
        </Button>
      </DialogActions>

      {errorMessage && (
        <ErrorModal
          open={!!errorMessage}
          onClose={() => setErrorMessage("")}
          message={errorMessage}
        />
      )}

      {showConfirmCancel && (
        <ConfirmCancelModal
          open={showConfirmCancel}
          onClose={() => setShowConfirmCancel(false)}
          onConfirm={handleConfirmCancel}
        />
      )}
    </Dialog>
  );
}
